import { sprite } from '../kit';
import { C, Pix } from '../pix';
import { registerIcons } from '../store';
import { gem, R, ramp, type Ramp } from './craft';

/**
 * Hand-made item pictures, lower gear: trousers and greaves (ขา), shoes and boots (เท้า) and rings
 * (แหวน). Each set piece wears its set's material so a set reads as one family on the bag grid:
 * the white ape's silver fur, the firebird's charcoal and embers, the thunder turtle's scutes and
 * the roaring-wave set's sea blue.
 */

const cotton = ramp('#c8b48a');
const hide = ramp('#7a5a3e');
const fur = ramp('#c8ccd8');
const char = ramp('#4a3a40');
const ember = ramp('#ff7a2a');
const scute = ramp('#5a7a5a');
const bolt = ramp('#8ad0ff');
const sea = ramp('#2a6aa8');
const foam = ramp('#d8f0f8');
const straw = ramp('#d0a85a');
const sky = ramp('#d8e4f4');

type Pt = [number, number];

// ── legs ──

/** Trousers seen from the front: a band, two legs that part below the seat, folds at the knee. */
function trousers(p: Pix, c: Ramp, band: Ramp): void {
  // Each leg balloons out to the knee and gathers at the cuff.
  const L: Pt[] = [
    [8, 8],
    [16, 8],
    [16, 14],
    [15, 20],
    [14, 27],
    [7, 27],
    [5, 21],
    [5, 14],
  ];
  const Rl: Pt[] = L.map(([x, y]) => [32 - x, y]);
  p.poly(L, c.d1);
  p.poly(Rl, c.b);
  // The lit side of each leg, and the shade inside the left one.
  p.poly(
    [
      [11, 9],
      [15, 9],
      [15, 14],
      [14, 20],
      [13, 26],
      [10, 26],
      [9, 20],
    ],
    c.b,
  );
  p.path(
    [
      [24, 9],
      [26, 14],
      [26, 21],
      [25, 26],
    ],
    c.l1,
  );
  p.path(
    [
      [22, 10],
      [23, 14],
      [23, 17],
    ],
    c.l1,
  );
  p.path(
    [
      [6, 14],
      [6, 21],
      [8, 26],
    ],
    c.d2,
  );
  // Seat seam and knee folds.
  p.line(16, 9, 16, 14, c.d2);
  p.path(
    [
      [7, 18],
      [10, 19],
      [13, 18],
    ],
    c.d2,
  );
  p.path(
    [
      [19, 18],
      [22, 19],
      [25, 18],
    ],
    c.d1,
  );
  p.px(22, 20, c.l1);
  // Waistband with its knot.
  p.rect(7, 5, 18, 3, band.b);
  p.line(7, 5, 24, 5, band.l1);
  p.line(7, 7, 24, 7, band.d1);
  p.rect(19, 5, 3, 3, band.d1);
  p.line(20, 8, 19, 12, band.b).line(21, 8, 22, 12, band.d1);
}

/** Wraps at the ankles: crossing bands on both cuffs. */
function legWraps(p: Pix, w: Ramp): void {
  for (const [x0, x1] of [
    [7, 14],
    [18, 25],
  ] as const) {
    p.rect(x0, 22, x1 - x0, 5, w.d1);
    for (let k = 0; k < 4; k++) p.line(x0 + k * 2, 26, x0 + k * 2 + 3, 22, w.l1);
    p.line(x0, 22, x1 - 1, 22, w.l2);
  }
}

/** A pair of shin guards: the far one behind and darker, both curved, with a knee cap. */
function greaves(p: Pix, m: Ramp, strap: Ramp, o: { cap?: Ramp } = {}): void {
  const guard = (x: number, y: number, s: Ramp, far: boolean) => {
    const pts: Pt[] = [
      [x, y + 3],
      [x + 2, y],
      [x + 8, y],
      [x + 10, y + 3],
      [x + 9, y + 20],
      [x + 5, y + 23],
      [x + 1, y + 20],
    ];
    p.poly(pts, far ? s.d1 : s.b);
    // The curve: a shade down the left, a lit ridge down the middle-right.
    p.line(x + 1, y + 4, x + 2, y + 19, far ? s.d2 : s.d1);
    p.line(x + 6, y + 2, x + 6, y + 20, far ? s.b : s.l1);
    if (!far) p.line(x + 7, y + 3, x + 7, y + 12, s.l2);
    for (const sy of [y + 6, y + 15]) {
      p.rect(x, sy, 11, 2, far ? strap.d2 : strap.d1);
      p.line(x + 1, sy, x + 9, sy, far ? strap.d1 : strap.b);
      p.px(x + 8, sy, R.bronze.l1);
    }
    const cap = o.cap ?? s;
    p.ell(x + 5, y + 2, 5, 2.5, far ? cap.d1 : cap.b);
    p.line(x + 3, y + 1, x + 8, y + 1, far ? cap.b : cap.l2);
  };
  guard(4, 5, m, true);
  guard(16, 4, m, false);
}

// ── feet ──

/**
 * A boot from the side, toe to the right and turned up: shaft, foot, a thick sole. `top` is how
 * high the shaft rises (lower for shoes).
 */
function boot(p: Pix, c: Ramp, sole: Ramp, o: { top?: number; toe?: Ramp; cuff?: Ramp } = {}): void {
  const top = o.top ?? 4;
  p.poly(
    [
      [8, top],
      [17, top],
      [18, 17],
      [24, 19],
      [28, 21],
      [28, 25],
      [6, 25],
      [6, 21],
    ],
    c.b,
  );
  // Shade at the back of the shaft and heel, a lit run down the front and over the instep.
  p.poly(
    [
      [8, top + 1],
      [10, top + 1],
      [10, 24],
      [7, 24],
      [7, 21],
    ],
    c.d1,
  );
  p.line(16, top + 1, 17, 17, c.l1);
  p.path(
    [
      [18, 18],
      [23, 19],
      [27, 21],
    ],
    c.l1,
  );
  p.path(
    [
      [11, 22],
      [20, 23],
    ],
    c.d1,
  );
  // Sole.
  p.rect(5, 25, 24, 3, sole.d1);
  p.line(6, 25, 28, 25, sole.b);
  p.line(5, 27, 28, 27, sole.d2);
  // Upturned toe.
  p.rect(28, 20, 2, 5, (o.toe ?? c).b);
  p.px(29, 19, (o.toe ?? c).l2);
  if (o.cuff) {
    p.rect(7, top, 12, 3, o.cuff.b);
    p.line(7, top, 18, top, o.cuff.l2);
    p.line(7, top + 2, 18, top + 2, o.cuff.d1);
  }
}

// ── rings ──

/** A ring seen from above and in front: the band as an ellipse, its back thinner and darker. */
function ringBand(p: Pix, b: Ramp, cy = 20, rx = 10, ry = 6, t = 3): void {
  p.ell(16, cy, rx, ry, b.d1);
  p.ell(16, cy + 0.6, rx - 0.6, ry - 0.4, b.b);
  p.ring(16, cy, rx - 0.5, ry - 0.5, b.l1, -2.6, -0.4);
  p.ring(16, cy, rx - 0.5, ry - 0.5, b.l2, -1.4, -0.9);
  for (let y = 0; y < 32; y++)
    for (let x = 0; x < 32; x++) {
      const dx = (x + 0.5 - 16) / (rx - t);
      const dy = (y + 0.5 - cy - 0.8) / (ry - t * 0.55);
      if (dx * dx + dy * dy < 1) p.erase(x, y);
    }
  p.ring(16, cy + 0.8, rx - t + 0.4, ry - t * 0.55 + 0.4, b.d2, 0.2, Math.PI - 0.2);
}

registerIcons('item', {
  // กางเกงผ้าฝ้ายรัดแข้ง: plain cotton trousers with wrapped shins.
  'cotton-trousers': (p) => {
    trousers(p, cotton, R.wood);
    legWraps(p, ramp('#8a7a5a'));
  },
  // สนับแข้งหนังหมาป่า: leather shin guards, grey wolf fur over the knee.
  'wolf-greaves': (p) => {
    greaves(p, hide, R.wood, { cap: ramp('#9a9aa2') });
    for (const x of [6, 9, 18, 21]) p.px(x, x < 16 ? 9 : 8, ramp('#9a9aa2').d1);
  },
  // รองเท้าฟางสาน: a pair of woven straw sandals from above, cord thongs over each.
  'straw-sandals': (p) => {
    const sandal = (cx: number, cy: number, lean: number, far: boolean) => {
      const s = far ? { d2: straw.d2, d1: straw.d2, b: straw.d1, l1: straw.b, l2: straw.l1 } : straw;
      p.ell(cx + lean, cy - 5, 5, 7, s.d1);
      p.ell(cx - lean, cy + 5, 4, 6, s.d1);
      p.ell(cx + lean + 0.4, cy - 5.4, 4, 6, s.b);
      p.ell(cx - lean + 0.4, cy + 4.6, 3, 5, s.b);
      // Plaited rows across the sole.
      for (let y = cy - 10; y <= cy + 9; y += 2) {
        const t = (y - cy) / 10;
        const x = cx - lean * t * 2;
        p.line(x - 3, y, x + 3, y, s.l1);
        p.px(x + 2, y + 1, s.d1).px(x - 2, y + 1, s.d1);
      }
      // The thong: a toe post and two cords back to the sides, a heel tie.
      const cord = far ? R.red.d1 : R.red.b;
      p.line(cx + lean, cy - 9, cx + lean - 4, cy - 2, cord);
      p.line(cx + lean, cy - 9, cx + lean + 4, cy - 2, cord);
      p.rect(cx + lean - 1, cy - 10, 2, 2, far ? R.red.d1 : R.red.l1);
      p.line(cx - lean - 3, cy + 6, cx - lean + 3, cy + 6, far ? R.wood.d1 : R.wood.b);
    };
    sandal(10, 16, 1, true);
    sandal(21, 15, 1, false);
  },
  // รองเท้าเหยียบเมฆ: a pale cloud boot, curled cloud trim at the cuff and toe.
  'cloud-boots': (p) => {
    boot(p, sky, R.ink, { cuff: R.azure, toe: R.azure });
    p.path(
      [
        [12, 13],
        [14, 11],
        [16, 13],
        [14, 15],
      ],
      R.azure.b,
    );
    p.path(
      [
        [19, 21],
        [22, 20],
        [24, 22],
      ],
      R.azure.d1,
    );
  },
  // แหวนทองเหลืองสลักลาย: a brass signet ring standing up, its oval face carved with a cloud mark.
  'bronze-ring': (p) => {
    const b = R.bronze;
    p.ell(16, 19, 9, 10, b.d1);
    p.ell(16.6, 18.6, 8, 9, b.b);
    p.ring(16, 19, 8.5, 9.5, b.l1, -1.4, 0.6);
    p.ring(16, 19, 8.5, 9.5, b.d2, 1.9, 3.4);
    p.ell(15.6, 20, 5, 6.5, b.d2);
    for (let y = 0; y < 32; y++)
      for (let x = 0; x < 32; x++) if (((x + 0.5 - 16) / 4.3) ** 2 + ((y + 0.5 - 20.6) / 5.8) ** 2 < 1) p.erase(x, y);
    // Engraved dashes round the band.
    for (let k = 0; k < 7; k++) {
      const a = 0.2 + k * 0.42;
      p.px(16 + Math.cos(a) * 7.3, 19 + Math.sin(a) * 8.2, b.d1);
    }
    // The signet face on top.
    p.ell(16, 8, 7, 4, b.d2);
    p.ell(16.4, 7.4, 6, 3.2, b.b);
    p.ring(16, 7.6, 6, 3.2, b.l2, -2.4, -0.2);
    sprite(p, 13, 6, ['.dd...', 'd..dd.', '.dd..d'], { d: b.d2 });
  },
  // แหวนหยกเขียวสงบจิต: a ring cut whole from jade, a gold collar round a jade cabochon.
  'jade-ring': (p) => {
    ringBand(p, R.jade, 21, 10, 6.5, 3.2);
    p.ell(16, 15, 5, 4, R.gold.d1);
    p.ell(16.3, 14.6, 4.2, 3.2, R.gold.b);
    gem(p, 16, 14, 3, ramp('#4ac890'));
    p.px(12, 17, R.gold.l2);
  },
  // สนับแข้งแขนยาว (ape set): light ape-hide guards, silver fur fringe at the knee.
  'wb-ape-greaves': (p) => {
    greaves(p, ramp('#6a5a4a'), ramp('#3a3a48'), { cap: fur });
    for (const x of [5, 8, 11, 17, 20, 23, 26]) p.line(x, x < 16 ? 9 : 8, x - 1, x < 16 ? 11 : 10, fur.l1);
  },
  // รองเท้าเท้าวานร: a thin-soled grip shoe with a split toe and a silver fur cuff.
  'wb-ape-boots': (p) => {
    boot(p, ramp('#6a5a4a'), ramp('#3a3a48'), { top: 9, cuff: fur });
    p.line(23, 19, 23, 24, ramp('#6a5a4a').d2);
    p.px(25, 20, ramp('#6a5a4a').l2);
    for (const x of [8, 11, 14, 17]) p.px(x, 8, fur.l2);
  },
  // สนับแข้งเกล็ดถ่าน (firebird set): charcoal scale guards, embers glowing in the seams.
  'wb-firebird-greaves': (p) => {
    greaves(p, char, R.lacquer, { cap: ember });
    for (const [x, y] of [
      [7, 13],
      [9, 18],
      [6, 24],
      [19, 12],
      [22, 17],
      [20, 23],
      [24, 22],
    ] as const) {
      p.px(x, y, ember.b);
      p.px(x + 1, y, ember.l2);
    }
  },
  // รองเท้าเถ้านิรันดร์: an ash-grey boot whose sole still glows like coals.
  'wb-firebird-boots': (p) => {
    boot(p, char, ember, { cuff: R.lacquer, toe: ember });
    p.rect(5, 26, 24, 1, ember.l1);
    for (const [x, y] of [
      [12, 14],
      [14, 8],
      [21, 21],
    ] as const)
      p.px(x, y, ember.l2);
  },
  // สนับแข้งเกล็ดอสนี (turtle set): shell-scute guards with a blue lightning vein.
  'wb-shell-greaves': (p) => {
    greaves(p, scute, ramp('#3a3a48'), { cap: scute });
    for (const ox of [4, 16]) {
      for (const y of [10, 17]) p.path([[ox + 2, y], [ox + 5, y + 2], [ox + 8, y]], scute.d2);
    }
    p.path(
      [
        [22, 7],
        [20, 12],
        [23, 15],
        [21, 22],
      ],
      bolt.l1,
    );
  },
  // รองเท้าตีนเต่าหิน: a squat boot on a heavy stone sole.
  'wb-shell-boots': (p) => {
    boot(p, scute, ramp('#7a7a86'), { top: 8, cuff: ramp('#3a3a48') });
    p.rect(4, 24, 26, 5, ramp('#7a7a86').d1);
    p.line(4, 24, 29, 24, ramp('#7a7a86').l1);
    for (const x of [8, 14, 20, 26]) p.line(x, 25, x - 1, 28, ramp('#7a7a86').d2);
    p.path([[13, 12], [15, 15], [13, 18], [16, 21]], bolt.l1);
  },
  // กางเกงคลื่นคำราม (wave set): sea-blue trousers, a foam wave curling along each cuff.
  'dg-set-legs': (p) => {
    trousers(p, sea, R.gold);
    for (const x0 of [7, 18]) {
      p.rect(x0, 24, 7, 3, foam.d1);
      sprite(p, x0, 22, ['..ff...', '.f..f.f', 'f.fffff'], { f: foam.l1 });
    }
  },
  // รองเท้าคลื่นคำราม: a sea-blue boot, foam crest round the cuff.
  'dg-set-feet': (p) => {
    boot(p, sea, R.ink, { cuff: foam, toe: R.gold });
    sprite(p, 8, 7, ['f.ff.ff.ff', '.f..f..f..'], { f: foam.l2 });
    p.path([[11, 15], [13, 13], [15, 15]], foam.b);
  },
  // รองเท้าเมฆาเหิน (legend): a white-and-gold boot riding a curled cloud, wind streaming behind.
  'u-cloud-boots': (p) => {
    const t = new Pix(32, 32);
    boot(t, sky, R.gold, { top: 3, cuff: R.gold, toe: R.gold });
    t.path(
      [
        [12, 10],
        [14, 8],
        [16, 10],
        [14, 12],
      ],
      R.gold.b,
    );
    p.blit(t, 0, -3);
    // A ruyi cloud under the sole.
    sprite(
      p,
      3,
      24,
      [
        '...wwww........wwww......',
        '..wWWWWw..www.wWWWWw.....',
        '.wWw..wWwwWWWwWw..wWw....',
        'wWWWwwWWWWWWWWWWwwWWWw...',
        '.bbbbbbbbbbbbbbbbbbbbbbb.',
        '..dddddddddddddddd..ddd..',
      ],
      { w: sky.b, W: C.white, b: R.azure.l1, d: R.azure.b },
    );
    for (const [y, x0] of [
      [8, 1],
      [13, 2],
      [18, 1],
    ] as const) {
      p.line(x0, y, x0 + 4, y, R.azure.l1);
      p.px(x0 + 4, y, C.white);
    }
  },
});
